import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useCart, type CartState } from './use-cart.js';

/**
 * One GET /api/cart for the whole app.
 *
 * The cart used to be fetched independently by the cart screen, the slot screen
 * and the calendar — three requests, three copies of one answer, and no way for
 * the shell to know anything about it. The released-hours banner is in the shell
 * because it has to follow the parent rather than wait on the screen they
 * happened to be on, so a fourth independent fetch would have meant a banner and
 * a cart screen that can disagree about what is held while they settle.
 *
 * `reload` matters as much as `state`. Adding a line, dropping one, or the clock
 * reaching zero all change the cart without remounting anything, so something
 * has to say "ask again" — and after this change that something must reach the
 * banner too, which is only possible with one shared source.
 */

interface CartContextValue {
  state: CartState;
  reload: () => void;
  /**
   * Whether the released-hours banner is on screen.
   *
   * The banner owns this - only it knows which rows are still actionable - but
   * the CART SCREEN needs it too, so it lives here rather than in either of
   * them. Ruled 30 September: while the banner is explaining that two hours were
   * released, the cart below it must not also say "Nothing in it yet." with a
   * "Pick a time" button. Both statements are true and they work against each
   * other, and the weaker action is the one at the bottom where the eye lands.
   */
  releasedVisible: boolean;
  setReleasedVisible: (visible: boolean) => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const { state, reload } = useCart();
  const [releasedVisible, setReleasedVisible] = useState(false);
  const value = { state, reload, releasedVisible, setReleasedVisible };
  const expiresAt = state.status === 'ready' && state.cart ? state.cart.expires_at : null;

  // THE HOLD LAPSING IS NOTICED HERE, NOT ON A SCREEN'S BUTTON.
  //
  // Found on the deployed build, 30 September: three lines held, sitting on the
  // calendar, the clock ran to 0:00 and nothing happened - no banner, no re-read.
  // The countdown lives on CartCta, and only the cart screen passed it an
  // onExpired; the calendar did not, and Home, Lessons and Account have no CTA to
  // hang one on at all. So a parent who walked away from any screen but the cart
  // got exactly the silence this feature exists to end.
  //
  // The banner lives in the frame so it can follow the parent. What NOTICES the
  // hold running out therefore has to live in the frame too, or it follows them
  // only as far as the screens that remembered to ask.
  //
  // One second past the expiry, so the server agrees it has gone rather than
  // racing it. Re-reading returns a null cart and the released hours, which
  // clears expiresAt - so this schedules once and does not loop.
  useEffect(() => {
    if (expiresAt === null) return;

    const left = new Date(expiresAt).getTime() - Date.now();
    if (left <= 0) {
      reload();
      return;
    }

    const timer = setTimeout(reload, left + 1000);
    return () => clearTimeout(timer);
  }, [expiresAt, reload]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/**
 * Throws outside the provider rather than returning a default.
 *
 * A default would mean a screen quietly behaving as though the cart were empty —
 * which on the cart screen is the "Nothing in it yet." lie this whole piece of
 * work exists to remove. Failing loudly in development is cheaper.
 */
export function useCartContext(): CartContextValue {
  const value = useContext(CartContext);
  if (!value) throw new Error('useCartContext used outside CartProvider.');
  return value;
}
